import { t } from '../i18n/index.ts';
import { qty as fmtQty } from '../lib/format.ts';
import { Button, IconButton } from './ui.tsx';

/**
 * Packs of a stock item: the larger units it is bought, received or counted in (a box of 24, a
 * pallet of 1,440). Stock, costs and bills stay in the item's own unit; a pack only turns what is
 * typed into it ("3 boxes" → 72 pieces).
 */

export interface Pack {
  name: string;
  qty: number;
}
export interface DraftPack {
  key: string;
  name: string;
  qty: string;
}
export const MAX_PACKS = 5;

const parse = (s: string): number | null => {
  const clean = s.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{0,3})?$/.test(clean)) return null;
  const n = Number(clean);
  return n > 0 ? n : null;
};

export const draftPacks = (packs: readonly Pack[] | undefined): DraftPack[] => (packs ?? []).map((p, i) => ({ key: `p${i}`, name: p.name, qty: fmtQty(p.qty) }));
export const savedPacks = (draft: readonly DraftPack[]): Pack[] => draft.map((p) => ({ name: p.name.trim(), qty: parse(p.qty)! }));

/** What is wrong with the packs as typed, or null. */
export function packsError(unit: string, draft: readonly DraftPack[]): string | null {
  const seen = new Set([unit.toLowerCase()]);
  for (const p of draft) {
    if (!p.name.trim()) return t('packs.need_name');
    if (parse(p.qty) === null) return t('packs.need_qty', { name: p.name.trim() });
    if (seen.has(p.name.trim().toLowerCase())) return t('packs.twice', { name: p.name.trim() });
    seen.add(p.name.trim().toLowerCase());
  }
  return null;
}

/** "Box of 24 pcs" */
export const packLabel = (p: Pack, unitText: string) => t('packs.of', { name: p.name, n: fmtQty(p.qty), unit: unitText });

/** The packs of an item, as rows of a name and how many units each holds. */
export function PacksEditor({ unitText, packs, onChange, error }: { unitText: string; packs: DraftPack[]; onChange: (p: DraftPack[]) => void; error: string | null }) {
  const set = (key: string, change: Partial<DraftPack>) => onChange(packs.map((p) => (p.key === key ? { ...p, ...change } : p)));
  return (
    <fieldset className="option-group">
      <legend className="field-label">
        {t('packs.title')} <span className="muted">{t('packs.hint', { unit: unitText })}</span>
      </legend>
      {packs.map((p) => (
        <div key={p.key} className="pack-row">
          <input className="input" value={p.name} maxLength={20} placeholder={t('packs.name_placeholder')} aria-label={t('packs.name')} onChange={(e) => set(p.key, { name: e.target.value })} />
          <span className="muted">{t('packs.holds')}</span>
          <input
            className="input pack-qty"
            inputMode="decimal"
            value={p.qty}
            placeholder="24"
            aria-label={t('packs.holds_of', { name: p.name || '…', unit: unitText })}
            onChange={(e) => set(p.key, { qty: e.target.value })}
          />
          <span className="muted">{unitText}</span>
          <IconButton icon="x" label={t('packs.remove', { name: p.name || '…' })} onClick={() => onChange(packs.filter((x) => x.key !== p.key))} />
        </div>
      ))}
      <div>
        <Button size="sm" icon="plus" onClick={() => onChange([...packs, { key: `n${Date.now()}`, name: packs.length ? '' : t('packs.box'), qty: '' }])} disabled={packs.length >= MAX_PACKS}>
          {t('packs.add')}
        </Button>
      </div>
      {error ? (
        <p className="field-msg" role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

/** Chooses what a quantity is typed in: the item's own unit or one of its packs. Value '' is the unit. */
export function PackSelect({ packs, unitText, value, onChange, label }: { packs: readonly Pack[]; unitText: string; value: string; onChange: (pack: string) => void; label: string }) {
  if (!packs.length) return <span className="muted pack-unit">{unitText}</span>;
  return (
    <select className="input pack-select" value={value} aria-label={label} onChange={(e) => onChange(e.target.value)}>
      <option value="">{unitText}</option>
      {packs.map((p) => (
        <option key={p.name} value={p.name}>
          {packLabel(p, unitText)}
        </option>
      ))}
    </select>
  );
}

/** How many units a quantity typed in a pack is. */
export const unitsOf = (packs: readonly Pack[], qty: number, pack: string) => (pack ? qty * (packs.find((p) => p.name === pack)?.qty ?? 1) : qty);
